/**
 * Helpers to build Schema.org JSON-LD blocks dynamically (so we never
 * leak preview URLs into production output and never inject fake
 * aggregate ratings).
 */

const BRAND = "Ever Clean Home";
const DEFAULT_DESCRIPTION = "Trusted residential cleaning services across Boston, MA · Connecticut · Rhode Island · Providence. Book online in 60 seconds.";

export function localBusinessSchema({ origin, reviewsCount = 0, ratingAvg = null }) {
  const business = {
    "@type": "LocalBusiness",
    "@id": `${origin}/#business`,
    name: BRAND,
    description: DEFAULT_DESCRIPTION,
    image: `${origin}/og-image.png`,
    url: origin,
    telephone: "+1-508-397-0721",
    email: "bookings@everclean.com",
    priceRange: "$$",
    areaServed: [
      { "@type": "City", name: "Boston, MA" },
      { "@type": "City", name: "Cambridge, MA" },
      { "@type": "City", name: "Providence, RI" },
      { "@type": "State", name: "Connecticut" },
      { "@type": "State", name: "Rhode Island" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "07:00",
        closes: "22:00",
      },
    ],
    address: {
      "@type": "PostalAddress",
      addressRegion: "MA",
      addressCountry: "US",
    },
  };
  // Only emit aggregateRating when there are REAL reviews (>=5) and a numeric average.
  if (reviewsCount >= 5 && typeof ratingAvg === "number" && ratingAvg > 0) {
    business.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: ratingAvg.toFixed(1),
      reviewCount: String(reviewsCount),
    };
  }
  return business;
}

export function serviceSchema({ origin }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Residential cleaning",
    provider: { "@id": `${origin}/#business` },
    areaServed: ["Boston", "Cambridge", "Providence", "Connecticut", "Rhode Island"],
    description:
      "Detail-obsessed residential cleaning — kitchens, bathrooms, bedrooms, laundry, windows and move-in/move-out deep cleans.",
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      url: `${origin}/book`,
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "USD",
        price: "89.00",
        valueAddedTaxIncluded: false,
      },
    },
  };
}

export function landingSchema({ origin, reviewsCount = 0, ratingAvg = null }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      localBusinessSchema({ origin, reviewsCount, ratingAvg }),
      {
        "@type": "Service",
        serviceType: "Residential cleaning",
        provider: { "@id": `${origin}/#business` },
        areaServed: ["Boston", "Cambridge", "Providence", "Connecticut", "Rhode Island"],
        description:
          "Detail-obsessed residential cleaning. Kitchens, bathrooms, bedrooms, laundry, windows and move-in/move-out deep cleans.",
        offers: { "@type": "Offer", priceCurrency: "USD", url: `${origin}/book` },
      },
    ],
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (items || []).map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function breadcrumbSchema(origin, items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${origin}${it.path}`,
    })),
  };
}
